import { Link } from 'react-router-dom';
import { LegalLayout, LegalSection } from './LegalLayout';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

const p = 'mb-3.5 text-[17px] leading-[1.65] text-[#c7c7cc] last:mb-0';
const li = 'text-[17px] leading-[1.6] text-[#c7c7cc]';

const SECTIONS = [
  {
    id: 'information-we-collect',
    title: 'Information we collect'
  },
  {
    id: 'how-we-use-your-information',
    title: 'How we use your information'
  },
  {
    id: 'how-we-share-your-information',
    title: 'How we share your information'
  },
  {
    id: 'international-data-transfers',
    title: 'International data transfers'
  },
  {
    id: 'data-retention',
    title: 'Data retention'
  },
  {
    id: 'your-rights',
    title: 'Your rights'
  },
  {
    id: 'cookies-local-storage',
    title: 'Cookies & local storage'
  },
  {
    id: 'data-security',
    title: 'Data security'
  },
  {
    id: 'childrens-privacy',
    title: "Children's privacy"
  },
  {
    id: 'changes-to-this-policy',
    title: 'Changes to this policy'
  },
  {
    id: 'data-protection-officer',
    title: 'Data Protection Officer'
  }
];

export function PrivacyPage() {
  usePageMeta('Privacy Policy', 'How RT Equipment & Haulage collects, uses, and protects your personal data.');
  return (
    <LegalLayout
      title="Privacy Policy"
      effective="July 1, 2026"
      sections={SECTIONS}
      other={{ to: '/terms', label: 'Read our Terms of Service' }}
      intro={
        <>
          <p className={p}>
              RT Equipment &amp; Haulage Company Limited ("RT Equipment &amp; Haulage," "we," "us," or "our")
              operates this website and customer booking platform (together, the "Service"), and is
              a data controller for the purposes of Jamaica's Data Protection Act, 2020 ("the Act").
              Our drivers and administrative staff access separate internal tools (including our
              mobile app) to fulfil bookings; this Policy focuses on the personal data we collect
              through the Service described here, though the same data-protection principles apply
              company-wide. This Privacy Policy explains what personal data we collect, why, how it
              is protected, and the rights the Act gives you over it. By using the Service, you
              agree to the collection and use of information as described here.
            </p>
        </>
      }
    >
        <LegalSection id="information-we-collect" num={1} title="Information we collect">
            <p className={p}>We collect the following categories of information:</p>
            <ul className="mt-1 mb-3.5 flex list-disc flex-col gap-2.5 pl-5 marker:text-accent">
              <li className={li}><span className="font-semibold text-white">Account information</span>: name, email address, phone number, and company name, collected when you create an account (via our authentication provider).</li>
              <li className={li}><span className="font-semibold text-white">Booking &amp; location information</span>: pickup and delivery addresses, GPS coordinates, equipment and service details, and scheduling information you provide when requesting a booking.</li>
              <li className={li}><span className="font-semibold text-white">Payment &amp; invoice information</span>: quote amounts, invoice status, and payment records. We do not store full payment card details ourselves.</li>
              <li className={li}><span className="font-semibold text-white">Photos</span>: equipment and fleet photos, hosted via our media storage provider.</li>
              <li className={li}><span className="font-semibold text-white">Usage &amp; device information</span>: pages visited, browser type, and general device information, collected automatically to keep the Service reliable and secure.</li>
            </ul>
        </LegalSection>

        <LegalSection id="how-we-use-your-information" num={2} title="How we use your information">
            <p className={p}>
              We only use personal data for the specific purposes it was collected for, and we do
              not process it in any way incompatible with those purposes. We use the information we
              collect to:
            </p>
            <ul className="mt-1 mb-3.5 flex list-disc flex-col gap-2.5 pl-5 marker:text-accent">
              <li className={li}>Create and manage your account, and verify your identity when you sign in.</li>
              <li className={li}>Process booking requests, generate quotes, and dispatch drivers.</li>
              <li className={li}>Provide real-time status notifications for active jobs.</li>
              <li className={li}>Generate invoices and reconcile payments.</li>
              <li className={li}>Respond to enquiries submitted through the Contact page or support channels.</li>
              <li className={li}>Maintain the security, performance, and integrity of the Service.</li>
              <li className={li}>Comply with applicable legal and regulatory obligations.</li>
            </ul>
            <p className={p}>
              We do not use your contact details for direct marketing unless you've consented to
              it, and every marketing communication (if any) will tell you how to opt out.
            </p>
        </LegalSection>

        <LegalSection id="how-we-share-your-information" num={3} title="How we share your information">
            <p className={p}>
              We do not sell your personal information. We share information only where necessary to
              operate the Service:
            </p>
            <ul className="mt-1 mb-3.5 flex list-disc flex-col gap-2.5 pl-5 marker:text-accent">
              <li className={li}><span className="font-semibold text-white">Our authentication provider</span>: authentication and session management.</li>
              <li className={li}><span className="font-semibold text-white">Our media storage provider</span>: hosting equipment and fleet photos.</li>
              <li className={li}><span className="font-semibold text-white">Our email delivery provider</span>: transactional email delivery (quote updates, status changes, verification codes).</li>
              <li className={li}>Between customers, assigned drivers, and administrative staff (via our internal tools), strictly as needed to fulfil a booking (e.g. a driver sees the pickup/delivery address and customer contact for their assigned job).</li>
              <li className={li}>Where required by law, court order, or to protect the rights, safety, or property of RT Equipment &amp; Haulage, our users, or the public.</li>
            </ul>
        </LegalSection>

        <LegalSection id="international-data-transfers" num={4} title="International data transfers">
            <p className={p}>
              Our authentication, media storage, and email delivery providers each operate outside Jamaica, so using the Service
              necessarily involves transferring some personal data to those countries. These
              transfers are necessary to perform our contract with you (creating your account,
              storing fleet photos, and delivering booking emails), and by using the Service you
              consent to this transfer, consistent with the Act. We only work with providers that
              maintain their own security and confidentiality safeguards for the data they process
              on our behalf.
            </p>
        </LegalSection>

        <LegalSection id="data-retention" num={5} title="Data retention">
            <p className={p}>
              We retain personal information for as long as your account is active or as needed to
              provide the Service, comply with our legal obligations (including tax and accounting
              requirements), resolve disputes, and enforce our agreements. You may request deletion
              of your account at any time by contacting us (see below).
            </p>
        </LegalSection>

        <LegalSection id="your-rights" num={6} title="Your rights">
            <p className={p}>Under the Act, you have the right to:</p>
            <ul className="mt-1 mb-3.5 flex list-disc flex-col gap-2.5 pl-5 marker:text-accent">
              <li className={li}><span className="font-semibold text-white">Access</span>: ask whether we're processing your personal data, and get a description of what we hold, why, and who we've disclosed it to.</li>
              <li className={li}><span className="font-semibold text-white">Rectification</span>: ask us to correct, amend, block, erase, or destroy inaccurate personal data.</li>
              <li className={li}><span className="font-semibold text-white">Object to processing</span>: ask us to stop or not begin processing that is causing you unwarranted damage or distress, is no longer relevant, or is being retained longer than permitted.</li>
              <li className={li}><span className="font-semibold text-white">Object to direct marketing</span>: opt out of marketing communications at any time, free of charge.</li>
              <li className={li}><span className="font-semibold text-white">Automated decisions</span>: not be subject to a decision based solely on automated processing that significantly affects you, without the chance to have it reconsidered.</li>
            </ul>
            <p className={p}>
              We will respond to a written request within 30 days. To exercise any of these rights,
              contact our Data Protection Officer using the details below. If you're not satisfied
              with our response, you may complain to Jamaica's Information Commissioner.
            </p>
        </LegalSection>

        <LegalSection id="cookies-local-storage" num={7} title="Cookies &amp; local storage">
            <p className={p}>
              We use essential cookies and browser local storage to keep you signed in and to
              remember your display preferences (such as light/dark mode). We do not use
              third-party advertising cookies.
            </p>
        </LegalSection>

        <LegalSection id="data-security" num={8} title="Data security">
            <p className={p}>
              We use industry-standard safeguards, including encrypted connections (HTTPS),
              access-controlled admin tooling, and role-based permissions, to protect your
              information. No method of transmission or storage is completely secure, and we cannot
              guarantee absolute security.
            </p>
        </LegalSection>

        <LegalSection id="childrens-privacy" num={9} title="Children's privacy">
            <p className={p}>
              The Service is intended for business and personal use by adults arranging equipment
              transport. We do not knowingly collect information from children under 18.
            </p>
        </LegalSection>

        <LegalSection id="changes-to-this-policy" num={10} title="Changes to this policy">
            <p className={p}>
              We may update this Privacy Policy from time to time. If we make material changes,
              we will update the effective date above and, where appropriate, notify you directly.
            </p>
        </LegalSection>

        <LegalSection id="data-protection-officer" num={11} title="Data Protection Officer">
            <p className={p}>
              Questions about this Privacy Policy, or requests to exercise any of the rights above,
              can be directed to our Data Protection Officer at{' '}
              <a href="mailto:rtequipment.co.ltd@gmail.com" className="text-accent hover:underline">
                rtequipment.co.ltd@gmail.com
              </a>{' '}
              or via our{' '}
              <Link to="/#contact" className="text-accent hover:underline">contact details</Link>.
            </p>
        </LegalSection>
    </LegalLayout>
  );
}
